// src/utils/googleCalendarApi.js
// Calls Supabase Edge Functions for real Google Calendar OAuth access.
// All MCP-based code removed — this is the authoritative API layer.

import { APP_TIMEZONE } from './googleCalendarSync';

const EDGE_BASE = 'https://ygkrlbftwhfshsoionbl.supabase.co/functions/v1';

// ── Auth header helper ────────────────────────────────────────────────────────
function authHeaders(userId) {
  return {
    'Content-Type': 'application/json',
    'x-user-id': userId || '',
  };
}

// ── Resolve current user ID from Supabase auth ────────────────────────────────
let _cachedUserId = null;
export function setCurrentUserId(id) { _cachedUserId = id; }
function getUserId() { return _cachedUserId || localStorage.getItem('mylo_user_id') || ''; }

// ── OAuth connect URL ─────────────────────────────────────────────────────────
export function getGoogleAuthUrl(userId) {
  const uid = userId || getUserId();
  return `${EDGE_BASE}/google-calendar-auth?user_id=${encodeURIComponent(uid)}`;
}

// ── Check if user has connected Google Calendar ───────────────────────────────
export async function checkGoogleCalendarConnected(userId) {
  const uid = userId || getUserId();
  try {
    const res = await fetch(`${EDGE_BASE}/google-calendar-events?action=calendars`, {
      headers: authHeaders(uid),
    });
    if (res.status === 403) return false; // NOT_CONNECTED
    return res.ok;
  } catch {
    return false;
  }
}

// ── Fetch all events across all calendars ─────────────────────────────────────
export async function fetchAllGoogleEvents(startDate, endDate) {
  const uid = getUserId();
  const daysBack = Math.round((Date.now() - startDate.getTime()) / 86400_000);
  const daysFwd  = Math.round((endDate.getTime() - Date.now()) / 86400_000);

  const res = await fetch(
    `${EDGE_BASE}/google-calendar-events?action=list&days_back=${daysBack}&days_fwd=${daysFwd}`,
    { headers: authHeaders(uid) }
  );

  if (res.status === 403) {
    const body = await res.json();
    if (body.code === 'NOT_CONNECTED') throw Object.assign(new Error('NOT_CONNECTED'), { code: 'NOT_CONNECTED' });
    throw new Error(body.error || 'Forbidden');
  }
  if (!res.ok) throw new Error(`fetchAllGoogleEvents: ${res.status}`);

  const data = await res.json();
  return data.events || [];
}

// ── Create event in Google Calendar ──────────────────────────────────────────
export async function createGoogleEvent(eventData, calendarId = 'primary') {
  const uid = getUserId();
  const res = await fetch(`${EDGE_BASE}/google-calendar-events`, {
    method: 'POST',
    headers: authHeaders(uid),
    body: JSON.stringify({ event: eventData, calendarId }),
  });
  if (!res.ok) throw new Error(`createGoogleEvent: ${res.status}`);
  const data = await res.json();
  return data.event || {};
}

// ── Delete event from Google Calendar ────────────────────────────────────────
export async function removeGoogleEvent(googleId, calendarId = 'primary') {
  const uid = getUserId();
  const res = await fetch(
    `${EDGE_BASE}/google-calendar-events?googleId=${encodeURIComponent(googleId)}&calendarId=${encodeURIComponent(calendarId)}`,
    { method: 'DELETE', headers: authHeaders(uid) }
  );
  if (!res.ok && res.status !== 404) throw new Error(`removeGoogleEvent: ${res.status}`);
}
